import { useState } from "react";
import {
  Trash2,
  Volume2,
  VolumeX,
  Check,
  Clock,
  PlayCircle,
  Maximize2,
  MoveHorizontal,
  Bell,
  Repeat,
  CalendarPlus,
  Pencil,
  Download,
  ExternalLink,
  Share2,
} from "lucide-react";
import { formatPretty } from "@/lib/time";
import { formatLeadTime } from "@/lib/format";
import { TONE_LABELS } from "@/lib/beeper";
import { useI18n } from "@/lib/i18n";

const TONE_LABEL_MAP = TONE_LABELS.reduce((acc, t) => {
  acc[t.v] = t.l;
  return acc;
}, {});
import { deleteSchedule } from "@/lib/api";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";
import { shareSchedule } from "@/lib/share";
import { toast } from "sonner";

const statusChip = (s) => {
  if (s.triggered) {
    return (
      <span className="inline-flex items-center gap-1 bg-neutral-200 brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
        <Check className="w-3 h-3" strokeWidth={3} /> Done
      </span>
    );
  }
  const now = Date.now();
  const t = new Date(s.scheduled_at).getTime();
  if (t <= now) {
    return (
      <span className="inline-flex items-center gap-1 bg-[#FF3311] text-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
        <PlayCircle className="w-3 h-3" strokeWidth={3} /> Due
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#E5FF00] brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
      <Clock className="w-3 h-3" strokeWidth={3} /> Pending
    </span>
  );
};

const CalendarMenu = ({ schedule }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid={`cal-menu-${schedule.id}`}
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        title="Add to calendar"
        className="bg-white brutal-border w-10 h-10 flex items-center justify-center brutal-shadow-sm brutal-interactive"
      >
        <CalendarPlus className="w-5 h-5" strokeWidth={3} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-30 bg-white brutal-border brutal-shadow p-2 min-w-[220px] space-y-2"
          onMouseDown={(e) => e.preventDefault()}
        >
          <a
            data-testid={`cal-google-${schedule.id}`}
            href={googleCalendarUrl(schedule)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 brutal-border bg-white p-2 font-mono text-xs uppercase brutal-shadow-sm brutal-interactive"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={3} />
            Google Calendar
          </a>
          <button
            data-testid={`cal-ics-${schedule.id}`}
            onClick={() => {
              downloadIcs(schedule);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 brutal-border bg-white p-2 font-mono text-xs uppercase brutal-shadow-sm brutal-interactive"
          >
            <Download className="w-4 h-4" strokeWidth={3} />
            Download .ics
          </button>
        </div>
      )}
    </div>
  );
};

export const ScheduleList = ({ schedules, onChanged, onTriggerNow, onEdit }) => {
  const { t } = useI18n();
  const handleDelete = async (id) => {
    try {
      await deleteSchedule(id);
      toast.success("SCHEDULE DELETED");
      onChanged?.();
    } catch (e) {
      toast.error("DELETE FAILED");
    }
  };

  const sorted = [...schedules].sort((a, b) => {
    if (a.triggered !== b.triggered) return a.triggered ? 1 : -1;
    return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
  });

  return (
    <div className="bg-white brutal-border brutal-shadow p-6 md:p-8">
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-display text-2xl md:text-3xl uppercase">
          {t("list.title")}
        </h2>
        <span
          data-testid="schedule-count"
          className="font-mono text-xs uppercase tracking-widest bg-[#0014C7] text-white brutal-border px-2 py-1"
        >
          {schedules.length} total
        </span>
      </div>

      {sorted.length === 0 ? (
        <div
          data-testid="empty-state"
          className="brutal-border border-dashed p-8 text-center font-mono text-sm uppercase text-neutral-500"
        >
          No flashes scheduled yet. Add one on the left.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="schedule-list">
          {sorted.map((s) => (
            <li
              key={s.id}
              data-testid={`schedule-item-${s.id}`}
              className="brutal-border p-4 bg-[#F2F2F0] brutal-shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {statusChip(s)}
                    {s.sound_enabled && s.alarm_tone && s.alarm_tone !== "off" ? (
                      <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                        <Volume2 className="w-3 h-3" strokeWidth={3} /> {TONE_LABEL_MAP[s.alarm_tone] || s.alarm_tone}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                        <VolumeX className="w-3 h-3" strokeWidth={3} /> Silent
                      </span>
                    )}
                    {s.display_mode === "scroll" ? (
                      <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                        <MoveHorizontal className="w-3 h-3" strokeWidth={3} /> Scroll
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                        <Maximize2 className="w-3 h-3" strokeWidth={3} /> Fit
                      </span>
                    )}
                    {s.repeat && s.repeat !== "none" && (
                      <span className="inline-flex items-center gap-1 bg-[#FFDD00] brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                        <Repeat className="w-3 h-3" strokeWidth={3} /> {s.repeat}
                      </span>
                    )}
                    {s.reminder_minutes ? (
                      <span className="inline-flex items-center gap-1 bg-[#00CCFF] brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                        <Bell className="w-3 h-3" strokeWidth={3} /> {formatLeadTime(s.reminder_minutes)} before{s.reminder_triggered ? " ✓" : ""}
                      </span>
                    ) : null}
                  </div>
                  <div className="font-display text-lg md:text-xl uppercase break-words leading-tight">
                    {s.message}
                  </div>
                  <div className="font-mono text-xs mt-2 text-neutral-700">
                    {formatPretty(s.scheduled_at)}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <CalendarMenu schedule={s} />
                  <button
                    data-testid={`share-${s.id}`}
                    onClick={async () => {
                      const res = await shareSchedule(s);
                      if (res.kind === "copied") toast.success("LINK COPIED");
                      else if (res.kind === "shared") toast.success("SHARED");
                      else if (res.kind === "fallback")
                        toast.error("CLIPBOARD BLOCKED");
                    }}
                    title="Share this flash"
                    className="bg-[#00CCFF] brutal-border w-10 h-10 flex items-center justify-center brutal-shadow-sm brutal-interactive"
                  >
                    <Share2 className="w-5 h-5" strokeWidth={3} />
                  </button>
                  <button
                    data-testid={`edit-${s.id}`}
                    onClick={() => onEdit?.(s)}
                    title="Edit schedule"
                    className="bg-white brutal-border w-10 h-10 flex items-center justify-center brutal-shadow-sm brutal-interactive"
                  >
                    <Pencil className="w-5 h-5" strokeWidth={3} />
                  </button>
                  {!s.triggered && (
                    <button
                      data-testid={`trigger-now-${s.id}`}
                      onClick={() => onTriggerNow?.(s)}
                      title={t("list.testFlashTitle")}
                      className="bg-[#E5FF00] brutal-border h-10 px-2 flex items-center gap-1 justify-center brutal-shadow-sm brutal-interactive font-mono text-[10px] uppercase tracking-widest"
                    >
                      <PlayCircle className="w-4 h-4" strokeWidth={3} />
                      {t("list.testFlash")}
                    </button>
                  )}
                  <button
                    data-testid={`delete-${s.id}`}
                    onClick={() => handleDelete(s.id)}
                    className="bg-white brutal-border w-10 h-10 flex items-center justify-center brutal-shadow-sm brutal-interactive"
                    title="Delete schedule"
                  >
                    <Trash2 className="w-5 h-5" strokeWidth={3} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ScheduleList;
